import type * as React from 'react'

const PRIMARY_ACTION_SELECTOR = '[data-dialog-primary-action="true"], [data-primary-action="true"]'
const FOOTER_SELECTOR = '[data-dialog-footer="true"]'
const ENTER_IGNORED_TARGET_SELECTOR = [
  'textarea',
  'input',
  'select',
  'button',
  'a[href]',
  '[role="button"]',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[data-dialog-enter-ignore="true"]',
].join(',')

const NESTED_LAYER_SELECTOR = [
  '[data-radix-popper-content-wrapper]',
  '[data-state="open"][role="menu"]',
  '[data-state="open"][role="listbox"]',
].join(',')

export function handlePrimaryActionOnEnter(
  event: React.KeyboardEvent<HTMLElement>,
  content: HTMLElement | null,
): boolean {
  if (!shouldHandleEnter(event, content)) {
    return false
  }

  const action = findPrimaryAction(content)
  if (!action) {
    return false
  }

  event.preventDefault()
  event.stopPropagation()
  action.click()
  return true
}

function shouldHandleEnter(
  event: React.KeyboardEvent<HTMLElement>,
  content: HTMLElement | null,
): content is HTMLElement {
  if (
    !content ||
    event.defaultPrevented ||
    event.key !== 'Enter' ||
    event.nativeEvent.isComposing ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey
  ) {
    return false
  }

  const target = event.target instanceof HTMLElement ? event.target : null
  if (!target || !content.contains(target) || target.closest(ENTER_IGNORED_TARGET_SELECTOR)) {
    return false
  }

  if (hasOpenNestedLayer(content)) {
    return false
  }

  return true
}

function findPrimaryAction(content: HTMLElement): HTMLElement | null {
  const marked = Array.from(content.querySelectorAll<HTMLElement>(PRIMARY_ACTION_SELECTOR))
    .reverse()
    .find(isActionUsable)
  if (marked) {
    return marked
  }

  const footers = Array.from(content.querySelectorAll<HTMLElement>(FOOTER_SELECTOR)).reverse()
  for (const footer of footers) {
    const candidates = Array.from(
      footer.querySelectorAll<HTMLElement>(
        'button, [role="button"], a[href], input[type="button"], input[type="submit"]',
      ),
    )
      .reverse()
      .filter((element) => !element.matches('[data-dialog-cancel-action="true"], [data-dialog-enter-ignore="true"]'))
    const action = candidates.find(isActionUsable)
    if (action) {
      return action
    }
  }

  return null
}

function isActionUsable(element: HTMLElement): boolean {
  if (element.getAttribute('aria-disabled') === 'true' || element.getAttribute('data-disabled') === 'true') {
    return false
  }
  if ('disabled' in element && element.disabled === true) {
    return false
  }
  return true
}

function hasOpenNestedLayer(content: HTMLElement): boolean {
  if (typeof document === 'undefined') {
    return false
  }

  return Array.from(document.querySelectorAll<HTMLElement>(NESTED_LAYER_SELECTOR)).some((layer) => {
    if (content.contains(layer)) {
      return false
    }
    return layer.offsetParent !== null || layer.getClientRects().length > 0
  })
}
